import React, { useState } from "react";
import { Box, Stack, InputAdornment, IconButton } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import TextInput from "qubCommon/Inputs/TextInput";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { PASSWORD_REGEX } from "qubCommon/helperMethods/passwordPolicy";
import { resetPassword } from "apis/controllers/users"; // ✅ δημιουργείς αυτή τη μέθοδο
import { enqueueSnackbar } from "notistack";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";

// ✅ 1️⃣ Schema σύμφωνο με backend
const schema = yup.object({
    password: yup
        .string()
        .matches(
            PASSWORD_REGEX,
            "Ο κωδικός πρέπει να έχει 8 χαρακτήρες, 1 πεζό, 1 κεφαλαίο, 1 αριθμό και 1 ειδικό σύμβολο"
        )
        .required("Απαιτείται νέος κωδικός"),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref("password")], "Οι κωδικοί δεν ταιριάζουν")
        .required("Απαιτείται επιβεβαίωση"),
});

const ResetPassword: React.FC = () => {
    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            password: "",
            confirmPassword: "",
        },
    });

    const { handleSubmit } = methods;

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { token } = useParams();

    // ✅ 2️⃣ Submit handler
    const onSubmit = async (data: any) => {
        if (!token) {
            enqueueSnackbar("Μη έγκυρο ή ληγμένο link επαναφοράς", { variant: "error" });
            return;
        }

        if (loading) return;
        setLoading(true);

        try {
            const payload = {
                token,
                password: data.password,
                confirmPassword: data.confirmPassword,
            };


            const res = await resetPassword(payload);

            if (res?.status === "success") {
                enqueueSnackbar(
                    res?.message || "Ο κωδικός άλλαξε με επιτυχία",
                    { variant: "success" }
                );

                methods.reset();

                // ✅ Redirect στο login μετά από επιτυχία
                setTimeout(() => navigate("/login"), 1200);
            } else {
                enqueueSnackbar(
                    res?.message || "Παρουσιάστηκε σφάλμα κατά την αλλαγή κωδικού",
                    { variant: res?.status || "error" }
                );
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar(
                "Παρουσιάστηκε σφάλμα κατά την αλλαγή κωδικού",
                { variant: "error" }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <FormProvider {...methods}>
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={2}>
                    {/* ✅ ΝΕΟΣ ΚΩΔΙΚΟΣ */}
                    <TextInput
                        name="password"
                        label="Νέος Κωδικός"
                        type={showPassword ? "text" : "password"}
                        otherInputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword((v) => !v)}>
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* ✅ ΕΠΙΒΕΒΑΙΩΣΗ */}
                    <TextInput
                        name="confirmPassword"
                        label="Επιβεβαίωση Νέου Κωδικού"
                        isFullWidth
                        type={showConfirm ? "text" : "password"}
                        otherInputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowConfirm((v) => !v)}>
                                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* ✅ SUBMIT */}
                    <LoadingButton
                        loading={loading}
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{
                            alignSelf: "flex-start",
                            mt: 1,
                            textTransform: "none",
                        }}
                    >
                        Αλλαγή Κωδικού
                    </LoadingButton>
                </Stack>
            </Box>
        </FormProvider>
    );
};

export default ResetPassword;
